import {useState} from 'react';
import {TouchableOpacity, Text, StyleSheet, ActivityIndicator, View, StyleProp, ViewStyle, Platform} from 'react-native';
import Alert from "@/lib/Alert";
import {useTranslation} from 'react-i18next';
import * as WebBrowser from 'expo-web-browser';
import {ExternalLink} from 'lucide-react-native';
import {useCurrentTheme} from '@/theming/useCurrentTheme';
import {ThemeType} from '@/theming/baseTheme';
import {createWebToken} from '@/data/app-data-api';
import {appConfig} from "@/app-config";

export function OpenWebAppButton({orgId, styleOverride}: { orgId: string, styleOverride: StyleProp<ViewStyle> }) {
    const {t} = useTranslation();
    const {theme} = useCurrentTheme();
    const [isLoading, setIsLoading] = useState(false);

    const styles = createStyles(theme);

    // Only render on mobile platforms
    if (Platform.OS === 'web') {
        return null;
    }

    const handleOpenWebApp = async () => {
        setIsLoading(true);
        try {
            // Create a web token
            const webTokenId = await createWebToken();

            // Construct the URL
            const webAppUrl = appConfig.webappUrl;
            if (!webAppUrl) {
                throw new Error('EXPO_PUBLIC_WEBAPP_URL is not configured');
            }

            const url = `${webAppUrl}/token/${webTokenId}?orgId=${orgId}`;

            // Open the web browser
            await WebBrowser.openBrowserAsync(url);
        } catch (error) {
            console.error('Failed to open web app:', error);
            Alert.alert(
                t('common.error', {defaultValue: 'Error'}),
                t('common.openWebAppError', {
                    defaultValue: 'Failed to open web app. Please try again.',
                })
            );
        } finally {
            setIsLoading(false);
        }
    };

    const webAppUrl = appConfig.webappUrl;

    return (
        <View style={[styles.container, styleOverride]}>
            <TouchableOpacity
                style={[styles.button, isLoading && styles.buttonDisabled]}
                onPress={handleOpenWebApp}
                disabled={isLoading}
                activeOpacity={0.7}
            >
                <View style={styles.buttonContent}>
                    {isLoading ? (
                        <ActivityIndicator color={theme.colors.primary} style={styles.iconLeft}/>
                    ) : (
                        <ExternalLink size={20} color={theme.colors.primary} style={styles.iconLeft}/>
                    )}
                    <Text style={styles.buttonText}>
                        {t('common:openWebApp')}
                    </Text>
                </View>
                <Text style={styles.subtext}>
                    {t('common:openWebAppSubtext', {url: webAppUrl})}
                </Text>
            </TouchableOpacity>
        </View>
    );
}

const createStyles = (theme: ThemeType) =>
    StyleSheet.create({
        container: {
            width: '100%',
        },
        button: {
            backgroundColor: theme.colors.surface,
            padding: 20,
            borderRadius: 12,
            borderWidth: 1,
            borderColor: theme.colors.border,
            alignItems: 'center',
            width: '100%',
            shadowColor: '#000',
            shadowOffset: {width: 0, height: 2},
            shadowOpacity: 0.05,
            shadowRadius: 4,
            elevation: 2,
        },
        buttonDisabled: {
            opacity: 0.6,
        },
        buttonContent: {
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
        },
        buttonText: {
            color: theme.colors.text,
            fontSize: 16,
            fontWeight: '600',
        },
        iconLeft: {
            marginRight: 12,
        },
        subtext: {
            marginTop: 12,
            fontSize: theme.fontSize.xs,
            color: theme.colors.textSecondary,
            textAlign: 'center',
            lineHeight: 16,
        },
    });
